import type { MgaSettlementColumnDef } from './useData';
import { isDeductionListColumn } from './settlementColumns';

export const BONDED_INCOMING_CODE = 'bonded:incoming';
export const BONDED_EXPENSE_CODE = 'bonded:expense';
export const CASH_INCOME_CODE = 'cash:income';
export const CASH_EXPENSE_CODE = 'cash:expense';

export type BooksSidebarItem = {
  code: string;
  name: string;
  kind: 'request' | 'bonded' | 'cash';
  hint?: string;
};

export type BooksSidebarGroupId =
  | 'cash'
  | 'advances'
  | 'allotments'
  | 'bonded'
  | 'bondedCrew'
  | 'deductions';

export const BOOKS_SIDEBAR_GROUP_ORDER: Array<{ id: BooksSidebarGroupId; title: string }> = [
  { id: 'cash', title: 'Cash / CTM' },
  { id: 'advances', title: 'Advances' },
  { id: 'allotments', title: 'Allotments' },
  { id: 'bonded', title: 'Bonded store' },
  { id: 'bondedCrew', title: 'Crew bonded charges' },
  { id: 'deductions', title: 'Deductions' },
];

export function isBondedViewCode(code: string) {
  return code === BONDED_INCOMING_CODE || code === BONDED_EXPENSE_CODE;
}

export function isCashViewCode(code: string) {
  return code === CASH_INCOME_CODE || code === CASH_EXPENSE_CODE;
}

export function booksGroupIdForItem(item: BooksSidebarItem): BooksSidebarGroupId {
  if (item.kind === 'cash') return 'cash';
  if (item.kind === 'bonded') return 'bonded';
  if (item.code === 'cashAdvanceOffice' || item.code === 'requestedCashByMaster') return 'advances';
  if (item.code === 'allotmentAmount') return 'allotments';
  if (
    item.code === 'cigarettesAmount' ||
    item.code === 'internetCardsAmount' ||
    item.code === 'otherBondedAmount'
  ) {
    return 'bondedCrew';
  }
  return 'deductions';
}

export function requestCategoriesFromDefs(
  categories: MgaSettlementColumnDef[] | null | undefined,
): MgaSettlementColumnDef[] {
  return (
    categories?.length
      ? categories
      : ([
          {
            id: 0,
            code: 'allotmentAmount',
            name: 'Allotments',
            columnType: 'DEDUCTION',
            sortOrder: 100,
            behavior: 'REQUEST_MIRROR',
            includeInTotal: true,
            isActive: true,
            showInGrid: true,
            sourceKey: 'REQUEST_LINES',
          },
          {
            id: 0,
            code: 'cashAdvanceOffice',
            name: 'Office advances',
            columnType: 'DEDUCTION',
            sortOrder: 120,
            behavior: 'REQUEST_MIRROR',
            includeInTotal: true,
            isActive: true,
            showInGrid: true,
            sourceKey: 'REQUEST_LINES',
          },
          {
            id: 0,
            code: 'requestedCashByMaster',
            name: 'Cash by master',
            columnType: 'DEDUCTION',
            sortOrder: 130,
            behavior: 'REQUEST_MIRROR',
            includeInTotal: true,
            isActive: true,
            showInGrid: true,
            sourceKey: 'REQUEST_LINES',
          },
          {
            id: 0,
            code: 'cigarettesAmount',
            name: 'Cigarettes',
            columnType: 'DEDUCTION',
            sortOrder: 80,
            behavior: 'REQUEST_MIRROR',
            includeInTotal: true,
            isActive: true,
            showInGrid: true,
            sourceKey: 'REQUEST_LINES',
          },
          {
            id: 0,
            code: 'internetCardsAmount',
            name: 'Internet cards',
            columnType: 'DEDUCTION',
            sortOrder: 85,
            behavior: 'REQUEST_MIRROR',
            includeInTotal: true,
            isActive: true,
            showInGrid: true,
            sourceKey: 'REQUEST_LINES',
          },
          {
            id: 0,
            code: 'otherBondedAmount',
            name: 'Other bonded',
            columnType: 'DEDUCTION',
            sortOrder: 90,
            behavior: 'REQUEST_MIRROR',
            includeInTotal: true,
            isActive: true,
            showInGrid: true,
            sourceKey: 'REQUEST_LINES',
          },
        ] as MgaSettlementColumnDef[])
  )
    .filter(isDeductionListColumn)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.code.localeCompare(b.code));
}

export function buildBooksSidebarItems(
  categories: MgaSettlementColumnDef[] | null | undefined,
): BooksSidebarItem[] {
  const requestCategories = requestCategoriesFromDefs(categories);
  const cashItems: BooksSidebarItem[] = [
    {
      code: CASH_INCOME_CODE,
      name: 'Income / CTM',
      kind: 'cash',
      hint: 'Cash received and CTM remittances',
    },
    {
      code: CASH_EXPENSE_CODE,
      name: 'Cash out',
      kind: 'cash',
      hint: 'Cash paid out (optional crew link updates Master adv.)',
    },
  ];
  const bondedItems: BooksSidebarItem[] = [
    {
      code: BONDED_INCOMING_CODE,
      name: 'Incoming stock',
      kind: 'bonded',
      hint: 'Bonded store receipts and opening stock',
    },
    {
      code: BONDED_EXPENSE_CODE,
      name: 'Stock out',
      kind: 'bonded',
      hint: 'Items taken out of bonded stock. Pick crew + payroll charge to update Cigarettes / Internet / Other in one step.',
    },
  ];
  const requestItems: BooksSidebarItem[] = requestCategories.map((c) => {
    const bondedCrewHint =
      c.code === 'cigarettesAmount' ||
      c.code === 'internetCardsAmount' ||
      c.code === 'otherBondedAmount'
        ? 'Crew payroll charges. Lines created from Stock out are locked here — edit under Bonded store → Stock out.'
        : undefined;
    return {
      code: c.code,
      name: c.name,
      kind: 'request' as const,
      hint: bondedCrewHint,
    };
  });
  const bondedRequestCodes = new Set([
    'cigarettesAmount',
    'internetCardsAmount',
    'otherBondedAmount',
  ]);
  const bondedCrew = requestItems.filter((i) => bondedRequestCodes.has(i.code));
  const otherRequests = requestItems.filter((i) => !bondedRequestCodes.has(i.code));
  return [...cashItems, ...otherRequests, ...bondedItems, ...bondedCrew];
}

export function buildBooksSidebarGroups(items: BooksSidebarItem[]) {
  const byGroup = new Map<BooksSidebarGroupId, BooksSidebarItem[]>();
  for (const item of items) {
    const gid = booksGroupIdForItem(item);
    const list = byGroup.get(gid) ?? [];
    list.push(item);
    byGroup.set(gid, list);
  }

  const bondedList = byGroup.get('bonded') ?? [];
  bondedList.sort((a, b) => {
    const rank = (c: string) =>
      c === BONDED_INCOMING_CODE ? 0 : c === BONDED_EXPENSE_CODE ? 1 : 2;
    return rank(a.code) - rank(b.code) || a.name.localeCompare(b.name);
  });
  byGroup.set('bonded', bondedList);

  const cashList = byGroup.get('cash') ?? [];
  cashList.sort((a, b) => {
    const rank = (c: string) => (c === CASH_INCOME_CODE ? 0 : 1);
    return rank(a.code) - rank(b.code);
  });
  byGroup.set('cash', cashList);

  return BOOKS_SIDEBAR_GROUP_ORDER.map((meta) => ({
    ...meta,
    items: byGroup.get(meta.id) ?? [],
  })).filter((g) => g.items.length > 0);
}

export function requestColumnCode(r: {
  columnCode?: string | null;
  requestType?: string | null;
}): string {
  if (r.columnCode) return r.columnCode;
  if (r.requestType === 'ALLOTMENT') return 'allotmentAmount';
  if (r.requestType === 'CASH_BY_MASTER') return 'requestedCashByMaster';
  return '';
}
